import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FILE_BUFFER_PREVIEW_QUERY_KEY } from 'hooks/useFileBufferPreviewQuery';
import { FILE_CONTENT_QUERY_KEY } from 'hooks/useFileContentQuery';

/**
 * Drops the cached file preview (metadata + bytes) so the previous version is no
 * longer displayed and the next open refetches from scratch.
 *
 * We `removeQueries` rather than `invalidateQueries`: a security change produces new
 * bytes (watermark / download / expiration) under the *same* cache key
 * ({ nodeId, version } — a watermark doesn't bump the version), and the signed url
 * lives only in the preview metadata. Merely invalidating keeps the stale entries and
 * refetches in the background, which lets the content query re-download from the old
 * signed url before the refreshed preview arrives. Removing them guarantees nothing
 * stale is shown and the content fetch waits for the fresh url.
 */
export default function useInvalidateFilePreviewCache() {
  const queryClient = useQueryClient();

  const invalidateFile = useCallback(
    (nodeId: string) => {
      // partial key match: the id is a subset of the full key object, so this
      // covers all versions / active-node variants for the file.
      queryClient.removeQueries({
        queryKey: [FILE_BUFFER_PREVIEW_QUERY_KEY, { nodeId }],
      });
      queryClient.removeQueries({
        queryKey: [FILE_CONTENT_QUERY_KEY, { id: nodeId }],
      });
    },
    [queryClient],
  );

  const invalidateAllFiles = useCallback(() => {
    queryClient.removeQueries({ queryKey: [FILE_BUFFER_PREVIEW_QUERY_KEY] });
    queryClient.removeQueries({ queryKey: [FILE_CONTENT_QUERY_KEY] });
  }, [queryClient]);

  return { invalidateFile, invalidateAllFiles };
}
